import { useBreadcrumb } from "features/breadcrumbs";
import {
  Box,
  Text,
  Flex,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import PageHeader from "components/Page/PageHeader";
import { useRouteMatch } from "react-router-dom";
import usePostPerfil from "features/perfil/hooks/usePostPerfil";
import useGetPerfil from "features/perfil/hooks/useGetPerfil";
import { useMemo } from "react";
import InputTablePermissoes from "components/Input/InputTablePermissoes";
import Field from "components/Input/Field";
import { useForm } from "react-hook-form";
import { Col, Row } from "antd";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { showError, showErrorAxios, showSuccess } from "infra/helpers/alerta";
import { useLoading } from "components/GlobalLoading";
import PrimaryButton from "components/Button/PrimaryButton";
import { useEffect } from "react";
import CardDefault from "components/Cards/CardDefault";
import { PermissaoEnum } from "core/resources/enums/permissao";
import { checkPermission } from "infra/helpers/permission";
import Container from "components/Container";

type FormPerfil = {
  dataFim: string;
  dataInicio: string;
  nome: string;
  permissoes: string[];
};

function PerfilDetalhes() {
  const history = useHistory();
  const route = useRouteMatch<{ id: string }>();
  const isPerfisEmpresa = route.path.includes("empresa/perfis");

  const { control, handleSubmit, reset } = useForm<FormPerfil>({
    defaultValues: {
      permissoes: [],
      dataInicio: moment().format("YYYY-MM-DD"),
    },
  });

  const { isLoading, data } = useGetPerfil(route.params.id);
  const { isLoading: isSaving, mutate: handleSave } = usePostPerfil();

  useLoading(isLoading || isSaving);

  const breadcrumb = useMemo(() => {
    if (isPerfisEmpresa) {
      return !route.params.id
        ? [
            { name: "Usuários", url: "/usuarios" },
            { name: "Perfis", url: "/empresa/perfis" },
            { name: "Criar perfil" },
          ]
        : [
            { name: "Usuários", url: "/usuarios" },
            { name: "Perfis", url: "/empresa/perfis" },
            { name: "Editar" },
          ];
    }

    return !route.params.id
      ? [
          { name: "Usuários", url: "/usuarios" },
          { name: "Perfis", url: "/cadastros/perfis" },
          { name: "Novo perfil" },
        ]
      : [
          { name: "Usuários", url: "/usuarios" },
          { name: "Perfis", url: "/cadastros/perfis" },
          { name: "Editar" },
        ];
  }, [isPerfisEmpresa, route.params.id]);

  useBreadcrumb(breadcrumb);

  useEffect(() => {
    reset(data);
  }, [data, reset]);

  const _handleSave = (values: FormPerfil) => {
    if (moment(values.dataFim).isBefore(moment(values.dataInicio))) {
      return showError(
        "Data de fim não pode ser anterior a data de inicio do perfifl"
      );
    }

    handleSave(values, {
      onSuccess: () => {
        showSuccess().then(() => {
          handleBack();
        });
      },
      onError: (err) => {
        showErrorAxios(err);
      },
    });
  };

  const handleBack = () => {
    if (isPerfisEmpresa) {
      history.push("/empresa/perfis");
    } else {
      history.push("/cadastros/perfis");
    }
  };

  const readonly = useMemo(() => {
    if (data?.admin) {
      return true;
    }

    if (
      route.params.id &&
      !checkPermission(PermissaoEnum.Backoffice.Administrativo.Perfil.Editar)
    ) {
      return true;
    }
  }, [data?.admin, route.params.id]);

  return (
    <Container>
      {data?.admin ? (
        <Alert status="warning" mb="16px">
          <AlertIcon />
          <AlertTitle>Aviso</AlertTitle>
          <AlertDescription>
            Este é um perfil administrador, não é possível editar!
          </AlertDescription>
        </Alert>
      ) : null}
      <form
        id="cadastrar-perfil"
        onSubmit={(e) => handleSubmit(_handleSave)(e)}
      >
        <Flex>
          <Flex>
            <PageHeader
              title={route.params.id ? "Editar Perfil" : "Novo Perfil"}
            />
          </Flex>
          <Flex flex={1} justifyContent="flex-end" gap="16px">
            <Button bg="white" onClick={handleBack}>
              Voltar
            </Button>
            <PrimaryButton disabled={!!readonly} type="submit" px="32px">
              Salvar
            </PrimaryButton>
          </Flex>
        </Flex>

        <CardDefault>
          <Row gutter={12}>
            <Box as={Col} xs={12} mb={4}>
              <Field
                label="Nome"
                control={control}
                name="nome"
                readonly={!!readonly}
                rules={{ required: "Campo obrigatório" }}
              />
            </Box>
            <Box as={Col} xs={6} mb={4}>
              <Field
                label="Início"
                control={control}
                name="dataInicio"
                readonly={!!readonly}
                rules={{ required: "Campo obrigatório" }}
                inputProps={{
                  type: "date",
                }}
              />
            </Box>

            <Box as={Col} xs={6} mb={4}>
              <Field
                label="Fim"
                control={control}
                name="dataFim"
                readonly={!!readonly}
                rules={{ required: "Campo obrigatório" }}
                inputProps={{
                  type: "date",
                }}
              />
            </Box>
          </Row>
        </CardDefault>

        <br />
        <br />
        <Text fontSize="16px">Selecione as permissões</Text>
        <br />

        {!isLoading ? (
          <Field
            control={control}
            name="permissoes"
            rules={{ required: "Campo obrigatório" }}
            render={({ field }) => {
              return (
                <InputTablePermissoes
                  origem="backoffice"
                  value={field.value}
                  readonly={!!readonly}
                  onChange={field.onChange}
                />
              );
            }}
          />
        ) : null}
      </form>
    </Container>
  );
}

export default PerfilDetalhes;
